import config from "../config";

/**
 * Meta Pixel (Facebook Pixel) tracking utility
 * Used for tracking conversions and page views for marketing campaigns
 *
 * TWO PIXELS ARE INSTALLED (both initialised in public/index.html):
 *
 *   1. config.metaPixel.pixelId          - HeliTaxii's own pixel
 *   2. config.metaPixel.partner.pixelId  - Thumby's pixel for the "Kerala by Air"
 *                                          campaign (KERALA_BY_SKY)
 *
 * Because both pixels live on the same page, every event is sent with
 * fbq('trackSingle', pixelId, ...) instead of fbq('track', ...). A plain
 * 'track' call would deliver the event to BOTH pixels, which would report
 * unrelated HeliTaxii bookings as Thumby conversions and vice versa.
 *
 * Our pixel:     PageView on every page (except /booking and /booking-confirmation),
 *                plus InitiateCheckout / Purchase for programs listed in
 *                metaPixel.trackedPrograms (currently empty - nothing fires).
 * Partner pixel: PageView only on Kerala surfaces (the Kerala tab of /bookwithus
 *                and the Kerala booking flow), plus InitiateCheckout on /booking
 *                and Purchase on /booking-confirmation, for the programs listed
 *                in metaPixel.partner.trackedPrograms.
 *
 * To add tracking for new programs:
 * 1. Add the program/type combination to metaPixel.trackedPrograms in config.js
 *    (or metaPixel.partner.trackedPrograms for the partner pixel)
 * 2. The tracking will automatically work for those programs
 */

/**
 * Check if Meta Pixel tracking is enabled
 * @returns {boolean}
 */
export const isMetaPixelEnabled = () => {
  return (
    config?.metaPixel?.enabled === true && typeof window.fbq === "function"
  );
};

/**
 * Check a schedule against a list of tracked program/type combinations
 * @param {Object} scheduleObj - The schedule object with program and type
 * @param {Array} trackedPrograms - List of { program, type } entries
 * @returns {boolean}
 */
const matchesTrackedPrograms = (scheduleObj, trackedPrograms = []) => {
  if (!scheduleObj) {
    return false;
  }

  const { program, type } = scheduleObj;

  return trackedPrograms.some(
    (tracked) =>
      tracked.program === program && (!tracked.type || tracked.type === type)
  );
};

/**
 * Check if a specific program/type should trigger tracking on our own pixel
 * @param {Object} scheduleObj - The schedule object with program and type
 * @returns {boolean}
 */
export const shouldTrackMetaPixel = (scheduleObj) => {
  const { metaPixel } = config;

  if (!metaPixel?.enabled) {
    return false;
  }

  return matchesTrackedPrograms(scheduleObj, metaPixel.trackedPrograms);
};

/**
 * Check if a specific program/type should trigger tracking on the partner pixel
 * @param {Object} scheduleObj - The schedule object with program and type
 * @returns {boolean}
 */
export const shouldTrackPartnerPixel = (scheduleObj) => {
  const { metaPixel } = config;

  if (!metaPixel?.enabled || !metaPixel?.partner?.pixelId) {
    return false;
  }

  return matchesTrackedPrograms(scheduleObj, metaPixel.partner.trackedPrograms);
};

/**
 * Send an event to a single pixel
 * @param {string} pixelId - The pixel the event belongs to
 * @param {string} eventName - Standard Meta event name
 * @param {Object} params - Event parameters (value, currency, ...)
 * @param {Object} options - fbq options, e.g. { eventID } for deduplication
 */
const trackSingle = (pixelId, eventName, params = null, options = null) => {
  if (!pixelId) {
    return;
  }

  if (options) {
    window.fbq("trackSingle", pixelId, eventName, params || {}, options);
  } else if (params) {
    window.fbq("trackSingle", pixelId, eventName, params);
  } else {
    window.fbq("trackSingle", pixelId, eventName);
  }
};

/**
 * Track a PageView event on our own pixel
 * Base code - fires on all pages except booking and booking-confirmation
 * @param {Object} scheduleObj - Optional schedule object for logging
 */
export const trackPageView = (scheduleObj = null) => {
  if (!isMetaPixelEnabled()) {
    return;
  }

  trackSingle(config.metaPixel.pixelId, "PageView");
  console.log("Meta Pixel: PageView tracked", {
    program: scheduleObj?.program,
    type: scheduleObj?.type,
  });
};

/**
 * Track a PageView on the partner (Kerala by Air) pixel only
 * Called from Kerala-specific surfaces - the partner pixel deliberately does
 * not receive PageView for the rest of the site.
 * @param {string} surface - Label describing where this was fired from (logging only)
 */
export const trackPartnerPageView = (surface = "") => {
  if (!isMetaPixelEnabled()) {
    return;
  }

  const partnerPixelId = config.metaPixel?.partner?.pixelId;

  if (!partnerPixelId) {
    return;
  }

  trackSingle(partnerPixelId, "PageView");
  console.log("Meta Pixel (partner): PageView tracked", { surface });
};

/**
 * Track InitiateCheckout event
 * Called when user lands on the booking page for tracked programs
 * @param {Object} scheduleObj - Schedule object to check if tracking should fire
 */
export const trackInitiateBooking = (scheduleObj = null) => {
  if (!isMetaPixelEnabled()) {
    return;
  }

  const { metaPixel } = config;
  const logContext = {
    program: scheduleObj?.program,
    type: scheduleObj?.type,
  };

  if (shouldTrackMetaPixel(scheduleObj)) {
    trackSingle(metaPixel.pixelId, "PageView");
    trackSingle(metaPixel.pixelId, "InitiateCheckout");
    console.log("Meta Pixel: PageView + InitiateCheckout tracked", logContext);
  } else {
    console.log(
      "Meta Pixel: Skipping InitiateCheckout - program not tracked",
      logContext
    );
  }

  if (shouldTrackPartnerPixel(scheduleObj)) {
    const partnerPixelId = metaPixel.partner.pixelId;
    trackSingle(partnerPixelId, "PageView");
    trackSingle(partnerPixelId, "InitiateCheckout");
    console.log(
      "Meta Pixel (partner): PageView + InitiateCheckout tracked",
      logContext
    );
  }
};

/**
 * Track Purchase event
 * Called when booking is confirmed/completed for tracked programs
 * @param {Object} bookingData - Booking details for the event
 * @param {string} bookingData.bookingId - Used as the deduplication event ID
 * @param {number} bookingData.amount - Grand total of the booking
 * @param {number} bookingData.seats - Number of tickets booked
 * @param {Object} bookingData.schedule - Schedule object (program/type)
 */
export const trackBooked = (bookingData = {}) => {
  if (!isMetaPixelEnabled()) {
    return;
  }

  const { metaPixel } = config;
  const { bookingId, amount, seats, schedule } = bookingData;
  const logContext = {
    bookingId,
    amount,
    program: schedule?.program,
    type: schedule?.type,
  };

  const purchaseParams = {
    value: amount || 0,
    currency: metaPixel.currency || "INR",
    content_type: "product",
    ...(schedule?.program && { content_name: schedule.program }),
    ...(seats && { num_items: seats }),
  };

  // A booking ID makes the event deduplicable, so a refresh of the
  // confirmation page is not counted as a second purchase.
  const options = bookingId ? { eventID: `purchase_${bookingId}` } : null;

  if (shouldTrackMetaPixel(schedule)) {
    trackSingle(metaPixel.pixelId, "PageView");
    trackSingle(metaPixel.pixelId, "Purchase", purchaseParams, options);
    console.log("Meta Pixel: PageView + Purchase tracked", logContext);
  } else {
    console.log("Meta Pixel: Skipping Purchase - program not tracked", logContext);
  }

  if (shouldTrackPartnerPixel(schedule)) {
    const partnerPixelId = metaPixel.partner.pixelId;
    trackSingle(partnerPixelId, "PageView");
    trackSingle(partnerPixelId, "Purchase", purchaseParams, options);
    console.log("Meta Pixel (partner): PageView + Purchase tracked", logContext);
  }
};
